import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import {
  signInWithEmail,
  writePendingCheckoutLocal,
  PendingCheckout,
} from '../lib/auth';
import { isNativeApp } from '../lib/nativeShell';

export function Login() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // If the user bounced here from Signup (email already exists) with the
  // pricing-page plan still in the URL, we stash it so the post-login
  // redirect goes to /checkout for the right tier — not /paywall where
  // they'd have to pick the plan again. Same shape as Signup.
  const [pendingCheckout, setPendingCheckout] = useState<PendingCheckout | null>(null);

  // Pick up a "your device was signed out" message stashed by the
  // device-revocation enforcer in lib/auth.ts. Shown as a friendly
  // notice on first load after the auto-sign-out.
  // Also prefill the email field from ?email=... so the Signup form's
  // "Sign in instead" CTA (which fires when the email already has an
  // account) drops the user straight onto the password field.
  useEffect(() => {
    try {
      const m = window.sessionStorage.getItem('sas_device_revoked_msg');
      if (m) {
        setNotice(m);
        window.sessionStorage.removeItem('sas_device_revoked_msg');
      }
    } catch { /* noop */ }
    try {
      const params = new URLSearchParams(window.location.search);
      const prefill = params.get('email');
      if (prefill) setEmail(prefill);
      // Arrived back in the app from the "Email confirmed" page.
      if (params.get('confirmed') === '1') {
        setNotice('Email confirmed ✅ Sign in to choose your plan and get started.');
      }
      // Read ?plan=&billing= — passed through from Signup's "Sign in
      // instead" bounce when the marketing pricing page sent an existing
      // customer to signup. Persist to localStorage so RequireSubscription
      // routes them to /checkout after login instead of /paywall.
      const tier = params.get('plan');
      const billing = params.get('billing');
      if (
        (tier === 'tier1' || tier === 'tier2' || tier === 'tier3') &&
        (billing === 'monthly' || billing === 'annual')
      ) {
        const pc: PendingCheckout = { tier, billing };
        setPendingCheckout(pc);
        writePendingCheckoutLocal(pc);
      }
    } catch { /* noop */ }
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmail(email, password);
      // If the login came through the marketing pricing → signup → "Sign
      // in instead" bounce, we have a pending checkout in state. Skip
      // straight to /checkout for a snappier redirect than letting
      // RequireSubscription bounce us via /paywall.
      // Post-login RequireSubscription is the belt-and-braces: even
      // logins without the URL params will get routed to /checkout if
      // user_metadata / localStorage has the pending_checkout stashed.
      nav(pendingCheckout ? '/checkout' : '/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title="Log in">
      <h1>Sign in</h1>
      <p className="muted">Welcome back. Let's get you on site.</p>

      {notice && (
        <div className="banner" style={{
          padding: 10, marginBottom: 12, fontSize: 13,
          background: '#FFF8E5', border: '1px solid #BF8E3D', borderRadius: 6,
        }}>
          {notice}
        </div>
      )}

      {error && <div className="banner banner-error">{error}</div>}

      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            enterKeyHint="next"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            enterKeyHint="go"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p style={{ textAlign: 'center', margin: '12px 0 0' }}>
          <Link to="/forgot-password" className="muted" style={{ fontSize: 13 }}>Forgotten your password?</Link>
        </p>
      </form>

      {/* Apple Guideline 3.1.1 (rejection 22 Sep 2026): no links to our
          website from inside the native app. New companies sign up in-app
          (Signup → Paywall → Apple In-App Purchase), same as the web. */}
      <p className="auth-footer">
        New here? <Link to={pendingCheckout ? `/signup?plan=${pendingCheckout.tier}&billing=${pendingCheckout.billing}` : '/signup'}>Create an account</Link>
      </p>
      {isNativeApp() && (
        <p
          className="muted"
          style={{ margin: '16px 0 0', fontSize: 12, lineHeight: 1.5, textAlign: 'center' }}
        >
          Already part of a team? Ask your company owner to invite you from
          Settings → Add users. You'll get an email with a link.
        </p>
      )}
    </AuthLayout>
  );
}
